import { defineStore } from 'pinia';
import { client } from '@/api';
import { ElMessage } from 'element-plus';
import { PaginatedTagsResponse, TagUpdateRequest, TagCreateRequest, TagRaw } from '@/api/generated-client/services/TagsApi';
import {
  GameTagState,
  GameTagsListResponse,
  GameTagsUpdateRequest,
  GameTagsUpdateResponse,
  GameTagsUpdateFailureReason,
  TagGamesRequest,
  TagGamesResponse,
  TagGamesFailureReason,
} from '@/api/generated-client/services/GameTagsApi';
import { getResponseErrorMessage } from '@/lib/getResponseErrorMessage';
import { getFieldsErrors } from '@/lib/getFieldsErrors';
import type { TagFieldsErrors, TagFormField } from '@/types/tags.types';

type Pagination = {
  page: number;
  limit: number;
  total: number;
  pageSizes?: number[];
};

type Filter = {
  state?: GameTagState | 'all'
};

type AssignationResult = {
  success: string[];
  fail: string[];
};

export type TagsState = {
  loading: boolean;
  updating: boolean
  error: string | null;
  fieldsErrors: TagFieldsErrors;
  assignationResult: AssignationResult,
  tags: TagRaw[];
  tagsPagination: Pagination;
  tagsFilter: Filter
};

type MessagePayload = {
  message: string;
  type: 'error' | 'success' | 'warning';
  duration?: number;
};

type AssignationResultsPayload = {
  allItems: string[];
  failedItems: FailedItems;
}

type PartialGameTagsUpdateResponse = Pick<GameTagsUpdateResponse, 'success' | 'failedItems'>;
type PartialGameTagsBindResponse = Pick<TagGamesResponse, 'success' | 'failedItems'>;
type FailureReasons = TagGamesFailureReason | GameTagsUpdateFailureReason;
type FailedItems = TagGamesResponse['failedItems'] | GameTagsUpdateResponse['failedItems'];

const MAP_REASON_TO_ERROR: Record<FailureReasons, (entity: string) => string> = {
  game_not_found: (entity) => `Game <b>${entity}</b> not found`,
  tag_incompatible: (entity) => `Tag is incompatible for <b>${entity}</b>`,
  tag_not_found: (entity) => `Tag <b>${entity}</b> not found`,
  tag_assignment_not_allowed: (entity) => `Tag assignment is not allowed for <b>${entity}</b>`,
  too_many_ui_badges: (entity) => `Too many tags assigned for <b>${entity}</b>`,
};

const MAP_API_FIELD_TO_FORM_FIELD: Record<string, TagFormField> = {
  kind: 'type',
  slug: 'slug',
  assignmentMode: 'assignmentMode',
  state: 'state',
  position: 'position',
  'value.position': 'position',
  imageUrl: 'image',
  'value.imageUrl': 'image',
};

const parseFieldsErrors = (err: unknown): TagFieldsErrors =>
  Object.entries(getFieldsErrors(err)).reduce<TagFieldsErrors>((acc, [field, messages]) => {
    const formField = MAP_API_FIELD_TO_FORM_FIELD[field];

    if (formField) {
      acc[formField] = [...(acc[formField] ?? []), ...messages];
    }

    return acc;
  }, {});

const getEntityId = (item: FailedItems[number]) => {
  if ('gameSlug' in item && typeof item.gameSlug === 'string') {
    return item.gameSlug;
  } if ('tagId' in item && typeof item.tagId === 'string') {
    return item.tagId;
  }
};
const parseAssignationError = (failedItems: FailedItems) => failedItems.reduce<string[]>((acc, item) => {
  const func = MAP_REASON_TO_ERROR[item.reason];
  const entity = getEntityId(item);
  const string = (entity && func(entity)) || '';

  acc.push(string);
  return acc;
}, []);

export const useTags = defineStore('tags', {
  state: (): TagsState => ({
    loading: false,
    updating: false,
    error: null,
    fieldsErrors: {},
    assignationResult: {
      success: [],
      fail: [],
    },
    tags: [],
    tagsPagination: {
      total: 0,
      page: 1,
      limit: 15,
      pageSizes: [15, 30, 45, 60],
    },
    tagsFilter: {},
  }),

  getters: {
    maxExistedPosition: (state) => state.tagsPagination.total,
  },

  actions: {
    showMessage({ message, type, duration = 5000 }: MessagePayload) {
      ElMessage[type]({ duration, message });
    },
    setLoading(value: boolean) {
      this.loading = value;
    },

    setUpdating(value: boolean) {
      this.updating = value;
    },

    setError(value: string | null) {
      this.error = value;
    },

    setFieldsErrors(err: unknown) {
      this.fieldsErrors = parseFieldsErrors(err);
    },

    clearFieldsErrors() {
      this.fieldsErrors = {};
    },

    clearFieldError(field: TagFormField) {
      const fieldsErrors = { ...this.fieldsErrors };
      delete fieldsErrors[field];
      this.fieldsErrors = fieldsErrors;
    },

    setTagsFilter(value: Filter) {
      this.tagsFilter = value;
    },

    setAssignationResult({ allItems, failedItems }: AssignationResultsPayload) {
      const failedIds = (failedItems ?? []).map((i) => getEntityId(i));
      this.assignationResult = {
        fail: parseAssignationError(failedItems ?? []),
        success: (allItems ?? []).filter((item) => !failedIds.includes(item)),
      };
    },

    setTagsPagination({ page, limit }: Partial<Pagination>) {
      this.tagsPagination.page = page ?? 1;
      this.tagsPagination.limit = limit ?? 15;

      this.getTagsList();
    },

    async getTagsList(): Promise<TagsState['tags']> {
      this.setError(null);
      this.setLoading(true);

      let data: PaginatedTagsResponse = {
        items: [],
        total: 0,
      };

      try {
        data = await client.tagsApi.getTags({
          page: this.tagsPagination.page,
          size: this.tagsPagination.limit,
          ...(this.tagsFilter.state && this.tagsFilter.state !== 'all' ? { state: this.tagsFilter.state } : {}),
        });

        this.tags = data.items;
        this.tagsPagination.total = data.total;
      } catch (err) {
        const message = 'Error during getting tags list';
        this.showMessage({ message, type: 'error' });
        this.setError(message);
      } finally {
        this.setLoading(false);
      }

      return data.items;
    },

    async createTag(payload: TagCreateRequest): Promise<TagRaw> {
      this.setError(null);
      this.clearFieldsErrors();
      this.setLoading(true);

      try {
        const tag = await client.tagsApi.createTag(payload);
        this.showMessage({ message: 'Tag created', type: 'success' });
        this.getTagsList();

        return tag;
      } catch (err) {
        const message = `Error during creating tag \n ${getResponseErrorMessage(err)}`;
        this.showMessage({ message, type: 'error' });
        this.setError(message);
        this.setFieldsErrors(err);

        throw err;
      } finally {
        this.setLoading(false);
      }
    },

    async getGameTags(gameSlug: string): Promise<GameTagsListResponse['items']> {
      this.setError(null);
      this.setLoading(true);

      let data: GameTagsListResponse = {
        items: [],
        gameSlug: '',
      };

      try {
        data = await client.gameTagsApi.getGameTags(gameSlug);
        data.items = data.items.filter((tag) => tag.kind === 'ui_badge');
      } catch (err) {
        const message = 'Error during getting tags list';
        this.showMessage({ message, type: 'error' });
        this.setError(message);
      } finally {
        this.setLoading(false);
      }

      return data.items;
    },

    async updateTag(id: string, payload: TagUpdateRequest): Promise<TagRaw> {
      this.setError(null);
      this.clearFieldsErrors();
      this.setLoading(true);

      try {
        const tag = await client.tagsApi.updateTag(id, payload);

        this.getTagsList();
        this.showMessage({ message: 'Tag updated', type: 'success' });

        return tag;
      } catch (err) {
        const message = `Error during updating the tag \n ${getResponseErrorMessage(err)}`;
        this.showMessage({ message, type: 'error' });
        this.setError(message);
        this.setFieldsErrors(err);

        throw err;
      } finally {
        this.setLoading(false);
      }
    },

    async updateGameTags(gameSlug: string, payload: GameTagsUpdateRequest): Promise<PartialGameTagsUpdateResponse> {
      this.setError(null);
      this.setUpdating(true);

      try {
        const data = await client.gameTagsApi.updateGameTags(gameSlug, payload);
        this.showMessage(data.success
          ? { message: 'Game tags updated successfully', type: 'success' }
          : { message: 'Game tags updated with problems', type: 'warning', duration: 7000 },
        );

        return data;
      } catch (err: unknown) {
        const message = getResponseErrorMessage(err);
        this.showMessage({ message, type: 'error' });
        this.setError(message);

        return { failedItems: [], success: false };
      } finally {
        this.setUpdating(false);
      }
    },

    async deleteTag(id: string): Promise<boolean> {
      this.setError(null);
      this.setLoading(true);

      try {
        await client.tagsApi.deleteTag(id);
        this.getTagsList();
        this.showMessage({ message: 'Tag deleted', type: 'success' });
      } catch (err) {
        const message = `Error during deleting the tag \n ${getResponseErrorMessage(err)}`;
        this.showMessage({ message, type: 'error' });
        this.setError(message);
      } finally {
        this.setLoading(false);
      }

      return true;
    },

    async addGamesToTag(tagId: string, payload: TagGamesRequest): Promise<PartialGameTagsBindResponse> {
      this.setError(null);
      this.setLoading(true);

      try {
        const data = await client.gameTagsApi.addGamesToTag(tagId, payload);
        this.setAssignationResult({
          allItems: payload.gameSlugs,
          failedItems: data.failedItems,
        });

        if (!data.success && data.affectedCount === 0 && data.requestedCount > 0) {
          throw new Error('');
        }

        this.showMessage(data.success
          ? { message: 'Tag has been added successfully', type: 'success' }
          : { message: 'Tag has been added with problems', type: 'warning', duration: 7000 },
        );

        return data;
      } catch (err) {
        const message = `Binding was not successful ${getResponseErrorMessage(err)}`;
        this.setError(message);
        this.showMessage({ message, type: 'error' });

        return { success: false, failedItems: [] };
      } finally {
        this.setLoading(false);
      }
    },

    async uploadTagImage(file: File): Promise<string> {
      this.clearFieldsErrors();

      try {
        return await client.tagsApi.uploadTagImage({ file });
      } catch (err) {
        const message = `Error during uploading tag image \n ${getResponseErrorMessage(err)}`;
        this.showMessage({ message, type: 'error' });
        this.setError(message);
        this.setFieldsErrors(err);

        throw err;
      }
    },
  },
});
